import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { distinctUntilChanged, first, skip, takeUntil } from 'rxjs/operators';
import swal from 'sweetalert2';

import { AuthService } from '../../auth/AuthService';
import {
  AuditErrorCategory,
  AuditErrorType,
  QuestionsService
} from '../../Services/questions.service';

interface ErrorTypeEditor {
  id: number | null;
  name: string;
  active: boolean;
}

interface ErrorCategoryEditor {
  id: number | null;
  name: string;
  active: boolean;
  defaultErrorTypeId: number | null;
}

@Component({
  selector: 'pm-error-configuration',
  templateUrl: './error-configuration.component.html',
  styleUrls: ['./error-configuration.component.css']
})
export class ErrorConfigurationComponent implements OnInit, OnDestroy {
  errorTypes: AuditErrorType[] = [];
  errorCategories: AuditErrorCategory[] = [];
  activeTab: 'types' | 'categories' = 'types';
  loading = false;
  saving = false;
  typeEditorOpen = false;
  categoryEditorOpen = false;
  currentClientId: number | null = null;

  typeEditor: ErrorTypeEditor = this.emptyTypeEditor();
  categoryEditor: ErrorCategoryEditor = this.emptyCategoryEditor();

  private destroyed = new Subject<void>();
  private viewDestroyed = false;

  constructor(
    private questionsService: QuestionsService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentClientId = this.authService.getClientId() || null;
    this.loadConfiguration();

    this.authService.clientId$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroyed))
      .subscribe((clientId: number) => {
        this.updateView(() => {
          this.currentClientId = clientId || null;
          this.errorTypes = [];
          this.errorCategories = [];
          this.closeEditors();
          this.loadConfiguration();
        });
      });
  }

  ngOnDestroy(): void {
    this.viewDestroyed = true;
    this.destroyed.next();
    this.destroyed.complete();
  }

  get canEdit(): boolean {
    return this.authService.hasRole('audit-edit');
  }

  get activeErrorTypes(): AuditErrorType[] {
    return this.errorTypes.filter(item => item.active);
  }

  get activeTypeCount(): number {
    return this.activeErrorTypes.length;
  }

  get activeCategoryCount(): number {
    return this.errorCategories.filter(item => item.active).length;
  }

  loadConfiguration(): void {
    if (!this.authService.getClientId() || this.loading) {
      return;
    }

    this.loading = true;
    forkJoin([
      this.questionsService.getErrorTypes(),
      this.questionsService.getErrorCategories()
    ]).pipe(first(), takeUntil(this.destroyed)).subscribe(
      ([types, categories]) => this.updateView(() => {
        this.errorTypes = (types || [])
          .map(item => ({ ...item, id: Number(item.id), clientId: Number(item.clientId) }))
          .sort((a, b) => a.name.localeCompare(b.name));

        this.errorCategories = (categories || [])
          .map(item => ({
            ...item,
            id: Number(item.id),
            clientId: Number(item.clientId),
            defaultErrorTypeId:
              item.defaultErrorTypeId === null || item.defaultErrorTypeId === undefined
                ? null
                : Number(item.defaultErrorTypeId)
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        this.loading = false;
      }),
      error => {
        this.updateView(() => { this.loading = false; });
        this.showError('Unable to Load Error Configuration', error);
      }
    );
  }

  selectTab(tab: 'types' | 'categories'): void {
    this.activeTab = tab;
  }

  openTypeCreate(): void {
    if (!this.canEdit) { return; }
    this.typeEditor = this.emptyTypeEditor();
    this.typeEditorOpen = true;
  }

  openTypeEdit(item: AuditErrorType): void {
    if (!this.canEdit) { return; }
    this.typeEditor = { id: item.id, name: item.name, active: item.active };
    this.typeEditorOpen = true;
  }

  openCategoryCreate(): void {
    if (!this.canEdit) { return; }
    this.categoryEditor = this.emptyCategoryEditor();
    this.categoryEditorOpen = true;
  }

  openCategoryEdit(item: AuditErrorCategory): void {
    if (!this.canEdit) { return; }
    this.categoryEditor = {
      id: item.id,
      name: item.name,
      active: item.active,
      defaultErrorTypeId: item.defaultErrorTypeId
    };
    this.categoryEditorOpen = true;
  }

  closeEditors(): void {
    this.typeEditorOpen = false;
    this.categoryEditorOpen = false;
    this.typeEditor = this.emptyTypeEditor();
    this.categoryEditor = this.emptyCategoryEditor();
    this.saving = false;
  }

  saveType(): void {
    if (!this.canEdit || this.saving) { return; }
    const name = (this.typeEditor.name || '').trim();
    if (!name) {
      this.showValidation('Error Type name is required.');
      return;
    }
    if (name.length > 100) {
      this.showValidation('Error Type name cannot exceed 100 characters.');
      return;
    }

    this.saving = true;
    const request = this.typeEditor.id === null
      ? this.questionsService.createErrorType({ name })
      : this.questionsService.updateErrorType(this.typeEditor.id, {
          name,
          active: this.typeEditor.active
        });

    request.pipe(first(), takeUntil(this.destroyed)).subscribe(
      () => this.handleSaveSuccess('Error Type saved successfully.'),
      error => this.handleSaveError(error)
    );
  }

  saveCategory(): void {
    if (!this.canEdit || this.saving) { return; }
    const name = (this.categoryEditor.name || '').trim();
    if (!name) {
      this.showValidation('Error Category name is required.');
      return;
    }
    if (name.length > 100) {
      this.showValidation('Error Category name cannot exceed 100 characters.');
      return;
    }

    const defaultErrorTypeId =
      this.categoryEditor.defaultErrorTypeId === null ||
      this.categoryEditor.defaultErrorTypeId === undefined ||
      this.categoryEditor.defaultErrorTypeId === ('' as any)
        ? null
        : Number(this.categoryEditor.defaultErrorTypeId);

    if (defaultErrorTypeId && !this.activeErrorTypes.some(item => item.id === defaultErrorTypeId)) {
      this.showValidation('Select an active Default Error Type.');
      return;
    }

    this.saving = true;
    const request = this.categoryEditor.id === null
      ? this.questionsService.createErrorCategory({ name, defaultErrorTypeId })
      : this.questionsService.updateErrorCategory(this.categoryEditor.id, {
          name,
          active: this.categoryEditor.active,
          defaultErrorTypeId
        });

    request.pipe(first(), takeUntil(this.destroyed)).subscribe(
      () => this.handleSaveSuccess('Error Category saved successfully.'),
      error => this.handleSaveError(error)
    );
  }

  trackById(index: number, item: AuditErrorType | AuditErrorCategory): number {
    return item.id;
  }

  private emptyTypeEditor(): ErrorTypeEditor {
    return { id: null, name: '', active: true };
  }

  private emptyCategoryEditor(): ErrorCategoryEditor {
    return { id: null, name: '', active: true, defaultErrorTypeId: null };
  }

  private handleSaveSuccess(message: string): void {
    this.updateView(() => this.closeEditors());
    swal.fire({
      icon: 'success',
      title: 'Error Configuration',
      text: message,
      showConfirmButton: false,
      timer: 1600
    });
    this.loadConfiguration();
  }

  private handleSaveError(error: any): void {
    this.updateView(() => { this.saving = false; });
    this.showError(
      error?.status === 409 ? 'Configuration Already Exists' : 'Unable to Save Error Configuration',
      error
    );
  }

  private showValidation(text: string): void {
    swal.fire({ icon: 'warning', title: 'Check Error Configuration', text });
  }

  private showError(title: string, error: any): void {
    swal.fire({
      icon: error?.status === 409 ? 'warning' : 'error',
      title,
      text:
        error?.error?.message ||
        error?.error?.detail ||
        error?.message ||
        'Something went wrong. Please try again.'
    });
  }

  private updateView(update: () => void): void {
    if (this.viewDestroyed) { return; }
    this.ngZone.run(() => {
      update();
      this.cdr.markForCheck();
      Promise.resolve().then(() => {
        if (!this.viewDestroyed) { this.cdr.detectChanges(); }
      });
    });
  }
}
